import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { Database, Download, Loader2, ArrowLeft } from 'lucide-react';
import { baixarBlob, zipStore } from '../utils/zipStore';

function textoCelula(valor) {
  if (valor == null || valor === '') return '—';
  if (typeof valor === 'boolean') return valor ? 'sim' : 'não';
  if (typeof valor === 'object') return JSON.stringify(valor, null, 2);
  return String(valor);
}

function tipoDe(valor) {
  if (valor == null) return null;
  if (typeof valor === 'boolean') return 'sim/não';
  if (typeof valor === 'number') return 'número';
  if (typeof valor === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(valor)) return 'data/hora';
    if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) return 'data';
    return 'texto';
  }
  if (Array.isArray(valor)) return 'lista';
  if (typeof valor === 'object') {
    if (valor._tipo === 'referencia') return 'referência';
    return 'objeto';
  }
  return 'texto';
}

function camposObservados(documentos, documentados) {
  const conhecidos = new Set(documentados.map((campo) => campo.nome));
  const mapa = new Map();
  documentos.forEach((doc) => {
    Object.entries(doc).forEach(([nome, valor]) => {
      if (nome === 'id' || conhecidos.has(nome) || valor == null) return;
      const atual = mapa.get(nome) || { nome, tipo: tipoDe(valor) || 'texto', ocorrencias: 0 };
      atual.ocorrencias += 1;
      if (!atual.tipo && tipoDe(valor)) atual.tipo = tipoDe(valor);
      mapa.set(nome, atual);
    });
  });
  return [...mapa.values()].sort((a, b) => a.nome.localeCompare(b.nome, 'pt-BR'));
}

function esquemaDaColecao(colecao, documentos) {
  const observados = camposObservados(documentos, colecao.campos || []);
  return {
    colecao: colecao.id,
    nome: colecao.nome,
    descricao: colecao.descricao,
    gerado_em: new Date().toISOString(),
    total_documentos: documentos.length,
    campos: [
      {
        nome: 'id',
        tipo: 'texto',
        obrigatorio: true,
        descricao: 'Identificador do documento no Firestore.',
        origem: 'sistema',
      },
      ...(colecao.campos || []).map((campo) => ({ ...campo, origem: 'modelo' })),
      ...observados.map((campo) => ({
        nome: campo.nome,
        tipo: campo.tipo,
        obrigatorio: false,
        descricao: `Presente em ${campo.ocorrencias} documento(s) exportado(s) e fora do modelo documentado.`,
        origem: 'observado',
        ocorrencias: campo.ocorrencias,
      })),
    ],
  };
}

function jsonBytes(valor) {
  return new TextEncoder().encode(JSON.stringify(valor, null, 2));
}

const PainelMigracaoPage = () => {
  const [colecoes, setColecoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [aberta, setAberta] = useState(null);
  const [amostra, setAmostra] = useState([]);
  const [amostraLoading, setAmostraLoading] = useState(false);
  const [exportando, setExportando] = useState('');
  const [progresso, setProgresso] = useState('');

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const resposta = await httpsCallable(functions, 'painelMigracaoResumo')({});
      setColecoes(resposta.data?.data?.colecoes || []);
    } catch (e) {
      setErro(e.message || 'Não foi possível carregar as coleções.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const abrir = async (colecao) => {
    setAberta(colecao);
    setAmostra([]);
    setAmostraLoading(true);
    setErro('');
    try {
      const resposta = await httpsCallable(functions, 'painelMigracaoAmostra')({ colecao: colecao.id });
      setAmostra(resposta.data?.data?.documentos || []);
    } catch (e) {
      setErro(e.message || 'Não foi possível carregar a amostra.');
    } finally {
      setAmostraLoading(false);
    }
  };

  const buscarTudo = async (colecao) => {
    const documentos = [];
    let cursor = '';
    const pagina = httpsCallable(functions, 'painelMigracaoPagina');
    do {
      const resposta = await pagina({ colecao: colecao.id, cursor });
      const lote = resposta.data?.data?.documentos || [];
      documentos.push(...lote);
      cursor = resposta.data?.data?.next || '';
      setProgresso(`${colecao.nome}: ${documentos.length} documento(s)`);
    } while (cursor);
    return documentos;
  };

  const arquivoColecao = async (colecao) => {
    const documentos = await buscarTudo(colecao);
    const dados = {
      colecao: colecao.id,
      gerado_em: new Date().toISOString(),
      total: documentos.length,
      documentos,
    };
    return [
      { nome: `${colecao.id}.esquema.json`, dados: jsonBytes(esquemaDaColecao(colecao, documentos)) },
      { nome: `${colecao.id}.dados.json`, dados: jsonBytes(dados) },
    ];
  };

  const gerarColecao = async (colecao, evento) => {
    evento?.stopPropagation();
    if (exportando) return;
    setExportando(colecao.id);
    setProgresso(colecao.nome);
    setErro('');
    try {
      const arquivos = await arquivoColecao(colecao);
      baixarBlob(zipStore(arquivos), `${colecao.id}-migracao.zip`);
    } catch (e) {
      setErro(e.message || `Não foi possível gerar os arquivos de ${colecao.nome}.`);
    } finally {
      setExportando('');
      setProgresso('');
    }
  };

  const gerarTudo = async () => {
    if (exportando || colecoes.length === 0) return;
    setExportando('tudo');
    setErro('');
    try {
      const arquivos = [];
      for (const colecao of colecoes) {
        const par = await arquivoColecao(colecao);
        par.forEach((arquivo) => arquivos.push({ ...arquivo, nome: `${colecao.id}/${arquivo.nome}` }));
      }
      const dia = new Date().toISOString().slice(0, 10);
      baixarBlob(zipStore(arquivos), `qualimanager-migracao-${dia}.zip`);
    } catch (e) {
      setErro(e.message || 'Não foi possível gerar o pacote completo.');
    } finally {
      setExportando('');
      setProgresso('');
    }
  };

  const grupos = useMemo(() => {
    const mapa = new Map();
    colecoes.forEach((colecao) => {
      const lista = mapa.get(colecao.grupo) || [];
      lista.push(colecao);
      mapa.set(colecao.grupo, lista);
    });
    return [...mapa.entries()];
  }, [colecoes]);

  const colunasAmostra = useMemo(() => {
    const chaves = new Set();
    amostra.forEach((doc) => Object.keys(doc).forEach((chave) => chaves.add(chave)));
    const resto = [...chaves].filter((chave) => chave !== 'id').sort((a, b) => a.localeCompare(b, 'pt-BR'));
    return ['id', ...resto];
  }, [amostra]);

  const observados = useMemo(
    () => (aberta ? camposObservados(amostra, aberta.campos || []) : []),
    [aberta, amostra]
  );

  if (aberta) {
    return (
      <div className="space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <button
              type="button"
              onClick={() => setAberta(null)}
              className="mb-2 flex items-center gap-1 text-sm font-medium text-blue-700 hover:underline"
            >
              <ArrowLeft className="h-4 w-4" /> Voltar às coleções
            </button>
            <h1 className="text-2xl font-bold text-gray-900">{aberta.nome}</h1>
            <p className="mt-1 max-w-3xl text-sm text-gray-500">{aberta.descricao}</p>
            <p className="mt-1 text-sm text-gray-700">
              {aberta.total == null ? 'Quantidade indisponível' : `${aberta.total} documento(s)`}
              <span className="text-gray-400"> · {aberta.id}</span>
            </p>
          </div>
          <button
            type="button"
            onClick={(evento) => gerarColecao(aberta, evento)}
            disabled={!!exportando}
            className="flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
          >
            {exportando === aberta.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            Gerar arquivos
          </button>
        </div>

        {erro && <p className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700">{erro}</p>}
        {progresso && <p className="text-sm text-gray-500">{progresso}</p>}

        <section className="overflow-x-auto rounded-xl border bg-white">
          <h2 className="border-b px-4 py-3 text-sm font-semibold text-gray-900">Modelo de dados</h2>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2">Campo</th>
                <th className="px-3 py-2">Tipo</th>
                <th className="px-3 py-2">Obrigatório</th>
                <th className="px-3 py-2">Descrição</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t">
                <td className="px-3 py-2 font-medium text-gray-900">id</td>
                <td className="px-3 py-2 text-gray-600">texto</td>
                <td className="px-3 py-2 text-gray-600">sim</td>
                <td className="px-3 py-2 text-gray-600">Identificador do documento no Firestore.</td>
              </tr>
              {(aberta.campos || []).map((campo) => (
                <tr key={campo.nome} className="border-t">
                  <td className="px-3 py-2 font-medium text-gray-900">{campo.nome}</td>
                  <td className="px-3 py-2 text-gray-600">{campo.tipo}</td>
                  <td className="px-3 py-2 text-gray-600">{campo.obrigatorio ? 'sim' : 'não'}</td>
                  <td className="px-3 py-2 text-gray-600">{campo.descricao}</td>
                </tr>
              ))}
              {observados.map((campo) => (
                <tr key={campo.nome} className="border-t bg-amber-50">
                  <td className="px-3 py-2 font-medium text-gray-900">{campo.nome}</td>
                  <td className="px-3 py-2 text-gray-600">{campo.tipo}</td>
                  <td className="px-3 py-2 text-gray-600">não</td>
                  <td className="px-3 py-2 text-gray-600">
                    Visto em {campo.ocorrencias} documento(s) da amostra, fora do modelo documentado.
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="overflow-x-auto rounded-xl border bg-white">
          <h2 className="border-b px-4 py-3 text-sm font-semibold text-gray-900">
            Amostra {amostraLoading ? '' : `(${amostra.length})`}
          </h2>
          {amostraLoading ? (
            <p className="flex items-center gap-2 px-4 py-6 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Carregando 20 documentos…
            </p>
          ) : amostra.length === 0 ? (
            <p className="px-4 py-6 text-sm text-gray-400">Esta coleção não tem documentos.</p>
          ) : (
            <table className="border-collapse text-xs">
              <thead className="bg-gray-50 text-left uppercase text-gray-500">
                <tr>
                  {colunasAmostra.map((coluna) => (
                    <th key={coluna} className="align-top p-0 font-medium">
                      <div className="w-56 max-w-56 whitespace-pre-wrap break-all px-3 py-2">{coluna}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {amostra.map((doc) => (
                  <tr key={doc.id} className="border-t align-top">
                    {colunasAmostra.map((coluna) => (
                      <td key={coluna} className="p-0 align-top">
                        <div className="w-56 max-w-56 whitespace-pre-wrap break-all px-3 py-2 text-gray-700">
                          {textoCelula(doc[coluna])}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <Database className="h-6 w-6 text-blue-700" /> Painel de Migração
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-500">
            Central de apoio à migração. Cada card é uma coleção. O arquivo de migração traz o esquema e todos os documentos, em dois JSON.
          </p>
        </div>
        <button
          type="button"
          onClick={gerarTudo}
          disabled={!!exportando || loading || colecoes.length === 0}
          className="flex items-center gap-2 rounded-lg bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
        >
          {exportando === 'tudo' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Gerar tudo
        </button>
      </div>

      {erro && <p className="rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm text-red-700">{erro}</p>}
      {progresso && <p className="text-sm text-gray-500">Gerando {progresso}</p>}

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Contando documentos…
        </p>
      ) : (
        grupos.map(([grupo, itens]) => (
          <section key={grupo}>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">{grupo}</h2>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {itens.map((colecao) => (
                <article
                  key={colecao.id}
                  className="flex cursor-pointer flex-col rounded-xl border bg-white p-4 shadow-sm hover:border-blue-300"
                  onClick={() => abrir(colecao)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-semibold text-gray-900">{colecao.nome}</h3>
                    <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800">
                      {colecao.total == null ? '—' : colecao.total}
                    </span>
                  </div>
                  <p className="mt-2 flex-1 text-sm text-gray-500">{colecao.descricao}</p>
                  <p className="mt-2 text-xs text-gray-400">{colecao.id}</p>
                  <button
                    type="button"
                    onClick={(evento) => gerarColecao(colecao, evento)}
                    disabled={!!exportando}
                    className="mt-3 flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    {exportando === colecao.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                    Gerar arquivos
                  </button>
                </article>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
};

export default PainelMigracaoPage;
